import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { store } from "./store";

type ComponentConfig = {
  import: () => Promise<{ default: React.ComponentType<any> }>;
  needsRouter?: boolean; // Defaults to false
};

type ComponentMap = {
  [key: string]: ComponentConfig;
};

// Map component names (from `data-hydrate-component` attribute) to their import paths.
const componentMap: ComponentMap = {
  LanguageSwitcher: {
    import: () =>
      import("../common/features/language/components/LanguageSwitcher"),
  },
  UserActions: {
    import: () => import("../parts/header/UserActions"),
  },
  FixedActionsRight: {
    import: () => import("../parts/footer/FixedActionsRight"),
  },
  FixedActionsLeft: {
    import: () => import("../parts/footer/FixedActionsLeft"),
  },
  MobileBottomNav: {
    import: () => import("../parts/footer/MobileBottomNav"),
  },
  SettingsPopup: {
    import: () => import("../parts/footer/SettingsPopup/SettingsPopup"),
  },
  AppPanels: {
    import: () => import("../parts/footer/AppPanels"),
  },
  GlobalToaster: {
    import: () => import("../common/components/Toaster/GlobalToaster"),
  },
  GlobalModal: {
    import: () =>
      import("../common/features/globalModal/components/GlobalModal"),
  },
  GlobalPopup: {
    import: () =>
      import("../common/features/globalPopup/components/GlobalPopup"),
  },
  ProductsArchive: {
    import: () =>
      import("../templates/ecommerce/product/components/ProductsArchive/ProductsArchive"),
    needsRouter: true,
  },
  ProductSingle: {
    import: () =>
      import("../templates/ecommerce/product/components/ProductSingle/ProductSingle"),
    needsRouter: true,
  },
  ProductModal: {
    import: () =>
      import("../templates/ecommerce/product/components/common/ProductModal/ProductModal"),
  },
  WishlistPage: {
    import: () =>
      import("../templates/ecommerce/wishlist/components/WishlistPage/WishlistPage"),
    needsRouter: true,
  },
  ComparePage: {
    import: () =>
      import("../templates/ecommerce/compare/components/Page/ComparePage"),
    needsRouter: true,
  },
  CartPage: {
    import: () =>
      import("../templates/ecommerce/cart/components/CartPage/CartPage"),
    needsRouter: true,
  },
  CheckoutPage: {
    import: () =>
      import("../templates/ecommerce/checkout/components/CheckoutPage"),
    needsRouter: true,
  },
  OrderPage: {
    import: () => import("../templates/ecommerce/order/components/OrderPage"),
    needsRouter: true,
  },
  AuthForms: {
    import: () => import("../templates/user/components/Auth/AuthForms"),
    needsRouter: true, // Router might be useful if you add a "Forgot Password" link
  },
  ProfileDashboard: {
    import: () =>
      import("../templates/user/components/Profile/ProfileDashboard"),
    needsRouter: true,
  },
  ForgotPasswordForm: {
    import: () =>
      import("../templates/user/components/Auth/ForgotPasswordForm"),
  },
  ResetPasswordForm: {
    import: () => import("../templates/user/components/Auth/ResetPasswordForm"),
  },
  FeaturedProductsSlider: {
    import: () => import("../partials/PageContent/FeaturedProductsSlider"),
  },
  MegaMenu: {
    import: () => import("../parts/header/MegaMenu"),
  },
  ContactForm: {
    import: () => import("../templates/contact/components/ContactForm"),
  },
  FranchiseForm: {
    import: () => import("../templates/franchise/components/FranchiseForm"),
  },
  CatalogueForm: {
    import: () =>
      import("../partials/PageContent/catalogueForm/components/CatalogueForm"),
  },
  CookieBanner: {
    import: () =>
      import("../common/features/cookieConsent/components/CookieBanner"),
  },
  RecentlyViewed: {
    import: () =>
      import("../partials/PageContent/recentlyViewed/components/RecentlyViewed"),
  },
};

/**
 * Finds all elements with the 'data-hydrate-component' attribute and renders
 * the corresponding React component into them.
 */
export const hydrateComponents = (): void => {
  const containers = document.querySelectorAll<HTMLElement>(
    "[data-hydrate-component]",
  );

  if (containers.length === 0) {
    return;
  }

  console.log(`[Hydrate] Found ${containers.length} component(s) to hydrate.`);

  containers.forEach((container) => {
    const componentName = container.dataset.hydrateComponent;
    const componentConfig = componentName ? componentMap[componentName] : null;

    if (componentConfig) {
      componentConfig
        .import()
        .then((module) => {
          const Component = module.default;
          const root = createRoot(container);
          const componentToRender = <Component />;

          // Conditionally wrap the component in BrowserRouter based on the config
          const finalRender = (
            <React.StrictMode>
              <Provider store={store}>
                {componentConfig.needsRouter ? (
                  <BrowserRouter>{componentToRender}</BrowserRouter>
                ) : (
                  componentToRender
                )}
              </Provider>
            </React.StrictMode>
          );

          root.render(finalRender);
          console.log(
            `[Hydrate] Component "${componentName}" hydrated successfully.`,
          );
        })
        .catch((err) => {
          console.error(
            `[Hydrate] Failed to load or render component "${componentName}":`,
            err,
          );
          try {
            const root = createRoot(container);
            root.render(
              <div
                style={{
                  padding: "1rem",
                  border: "1px solid red",
                  color: "red",
                }}
              >
                Error loading component: {componentName}.
              </div>,
            );
          } catch (renderError) {
            container.innerHTML = `Error loading component: ${componentName}.`;
          }
        });
    } else {
      console.warn(
        `[Hydrate] Component "${componentName}" not found in the component map.`,
      );
    }
  });
};
